import { DownloadOutlined, FileExcelOutlined } from '@ant-design/icons'
import { Modal, Upload, message } from 'antd'
import { useEffect, useState } from 'react'
import {
    downloadTestCaseTemplate,
    importTestCasesExcel,
} from '../../../redux/testRun/testRun.service'
import styles from './ImportTestCasesModal.module.css'

const EXCEL_EXTENSIONS = ['.xlsx', '.xls']
const EXCEL_MIME_TYPES = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
]
const UNSUPPORTED_FILE_ERROR = 'Only .xlsx or .xls files are supported'

const COLUMN_ERROR_PATTERN =
    /^(Missing column\(s\):|Unexpected column\(s\):)\s*(.*)$/i

const renderImportError = (err) => {
    const text = String(err || '').trim()
    const match = text.match(COLUMN_ERROR_PATTERN)
    const columns = match?.[2]
        ? match[2]
              .split(',')
              .map((column) => column.trim())
              .filter(Boolean)
        : []

    if (match && columns.length) {
        return (
            <li key={text}>
                <div>{match[1]}</div>
                <ul className={styles.columnList}>
                    {columns.map((column) => (
                        <li key={column}>{column}</li>
                    ))}
                </ul>
            </li>
        )
    }

    return <li key={text}>{text}</li>
}

const isSupportedExcelFile = (file) => {
    const fileName = (file?.name || '').toLowerCase()
    const fileType = (file?.type || '').toLowerCase()
    const hasValidExtension = EXCEL_EXTENSIONS.some((ext) =>
        fileName.endsWith(ext)
    )
    const hasValidMimeType = EXCEL_MIME_TYPES.includes(fileType)

    return hasValidExtension || hasValidMimeType
}

const ImportTestCasesModal = ({
    isOpen = false,
    setOpen = () => {},
    projectId,
    onSuccess = () => {},
}) => {
    const [fileList, setFileList] = useState([])
    const [loading, setLoading] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [importError, setImportError] = useState(null)

    useEffect(() => {
        if (!isOpen) {
            setFileList([])
            setImportError(null)
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return undefined

        const onKeyDown = (event) => {
            if (event.key !== 'Escape') return
            if (loading) return
            event.preventDefault()
            event.stopPropagation()
            handleClose()
        }

        window.addEventListener('keydown', onKeyDown, true)
        return () => window.removeEventListener('keydown', onKeyDown, true)
    }, [isOpen, loading, setOpen])

    const handleClose = () => {
        if (loading) return
        setFileList([])
        setImportError(null)
        setOpen(false)
    }

    const handleDownloadTemplate = async () => {
        setDownloading(true)
        try {
            const result = await downloadTestCaseTemplate()
            if (!result?.success) {
                message.error(result?.message || 'Failed to download template')
            }
        } finally {
            setDownloading(false)
        }
    }

    const handleBeforeUpload = (file) => {
        if (isSupportedExcelFile(file)) {
            return false
        }

        setImportError({
            title: UNSUPPORTED_FILE_ERROR,
            lines: [],
        })
        setFileList([])
        return Upload.LIST_IGNORE
    }

    const handleFileChange = ({ fileList: nextList }) => {
        const validFiles = nextList
            .filter((fileItem) =>
                isSupportedExcelFile(fileItem?.originFileObj || fileItem)
            )
            .slice(0, 1)

        if (validFiles.length) {
            setImportError(null)
        }

        setFileList(validFiles)
    }

    const handleImport = async () => {
        if (!fileList.length) {
            message.warning('Please select a file to import')
            return
        }

        const selectedFile = fileList[0].originFileObj || fileList[0]
        if (!isSupportedExcelFile(selectedFile)) {
            setImportError({
                title: UNSUPPORTED_FILE_ERROR,
                lines: [],
            })
            setFileList([])
            return
        }

        if (!projectId) {
            message.error('Project is required to import test cases')
            return
        }

        setLoading(true)
        setImportError(null)
        try {
            const res = await importTestCasesExcel({
                file: selectedFile,
                projectId,
            })
            const data = res?.data ?? {}

            if (data?.success) {
                message.success(data?.message || 'Test cases imported successfully')
                setFileList([])
                handleClose()
                onSuccess()
                return
            }

            const errorLines = Array.isArray(data?.errors) ? data.errors : []
            setImportError({
                title: data?.message || 'Import failed',
                lines: errorLines,
            })
        } catch {
            setImportError({
                title: 'Failed to import test cases',
                lines: [],
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            title={null}
            open={isOpen}
            onCancel={handleClose}
            footer={null}
            width={560}
            centered
            destroyOnClose
            keyboard={!loading}
            maskClosable={!loading}
            className={styles.modal}
        >
            <div className={styles.shell}>
                <div className={styles.header}>
                    <div className={styles.headerIcon}>
                        <FileExcelOutlined />
                    </div>
                    <div>
                        <h3 className={styles.title}>Import test cases</h3>
                        <p className={styles.subtitle}>
                            Upload an Excel file (.xlsx, .xls) to add multiple test
                            cases to this project at once.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    className={styles.templateButton}
                    onClick={handleDownloadTemplate}
                    disabled={downloading}
                >
                    <DownloadOutlined />
                    <span>
                        {downloading ? 'Downloading...' : 'Download template'}
                    </span>
                </button>

                <Upload.Dragger
                    className={styles.uploader}
                    fileList={fileList}
                    onChange={handleFileChange}
                    beforeUpload={handleBeforeUpload}
                    accept=".xlsx,.xls"
                    maxCount={1}
                    onRemove={() => setFileList([])}
                >
                    <div className={styles.uploaderInner}>
                        <FileExcelOutlined className={styles.uploaderIcon} />
                        <p className={styles.uploaderTitle}>
                            Select or drop an Excel file
                        </p>
                        <p className={styles.uploaderHint}>
                            Columns: Title, Preconditions, Testdata, Steps,
                            Description, Expected Result, Sprint, Tags
                        </p>
                    </div>
                </Upload.Dragger>

                {importError ? (
                    <div className={styles.errorBox} role="alert">
                        <div className={styles.errorTitle}>
                            {importError.title}
                        </div>
                        {importError.lines?.length ? (
                            <ul className={styles.errorList}>
                                {importError.lines.map((err) =>
                                    renderImportError(err)
                                )}
                            </ul>
                        ) : null}
                    </div>
                ) : null}

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.importButton}
                        onClick={handleImport}
                        disabled={!fileList.length || loading}
                    >
                        {loading ? 'Importing...' : 'Import'}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default ImportTestCasesModal
